import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { gbp, longDate } from "@/lib/api";

const Row = ({ u }) => (
  <Link
    to={`/book/${u.clinic_slug}?service=${u.service_id}&practitioner=${u.practitioner_id}&date=${u.date}&time=${u.time}`}
    data-testid={`upcoming-item-${u.clinic_slug}-${u.date}`}
    className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-3 pr-5 lift"
  >
    <img src={u.image} alt={u.clinic_name} className="h-20 w-24 sm:w-32 rounded-xl object-cover" />
    <div className="flex-1 min-w-0">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-accent"><Clock className="h-3.5 w-3.5" />{u.time} · {u.open_slots} slots open</p>
      <p className="font-display font-bold truncate group-hover:text-primary transition-colors">{u.clinic_name}</p>
      <p className="text-xs text-muted-foreground truncate">{u.service_name} with {u.practitioner_name}</p>
      <p className="text-xs text-muted-foreground">{u.discipline_name} · {u.city}</p>
    </div>
    <div className="text-right shrink-0">
      <p className="font-semibold">{gbp(u.price)}</p>
      <p className="text-xs text-muted-foreground">from</p>
    </div>
  </Link>
);

export const UpcomingFeed = ({ items, disciplines }) => {
  const [city, setCity] = useState("all");
  const [disc, setDisc] = useState("all");
  const cities = useMemo(() => [...new Set(items.map((i) => i.city))].sort(), [items]);
  const filtered = items.filter((i) => (city === "all" || i.city === city) && (disc === "all" || i.discipline === disc));
  const groups = filtered.reduce((acc, i) => ({ ...acc, [i.date]: [...(acc[i.date] || []), i] }), {});

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="w-44 rounded-full bg-card" data-testid="upcoming-city-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap gap-2">
          {[{ slug: "all", name: "All" }, ...disciplines].map((d) => (
            <button key={d.slug} onClick={() => setDisc(d.slug)} data-testid={`upcoming-discipline-filter-${d.slug}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${disc === d.slug ? "bg-primary text-white border-primary" : "bg-card border-border hover:border-primary/50"}`}>
              {d.name}
            </button>
          ))}
        </div>
      </div>
      {Object.keys(groups).length === 0 && <p className="text-muted-foreground" data-testid="upcoming-empty">No open appointments match those filters.</p>}
      {Object.entries(groups).map(([day, list]) => (
        <div key={day} className="grid md:grid-cols-[220px_1fr] gap-4">
          <div className="md:sticky md:top-24 self-start">
            <p className="font-display font-bold text-lg">{longDate(day)}</p>
            <p className="text-sm text-muted-foreground">{list.length} clinic{list.length > 1 ? "s" : ""} available</p>
          </div>
          <div className="grid gap-3">{list.map((u) => <Row key={u.clinic_id + u.date} u={u} />)}</div>
        </div>
      ))}
    </div>
  );
};
